import { useState } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import StorageIcon from '@mui/icons-material/Storage';

import { BrowserTitle, Spinner } from '../components';
import integrationServices from '../services/integrationServices';

export const Archive = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleSearch = async () => {
    if (!query || query.length < 2) {
      toast.warning('Please enter at least 2 characters');
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await integrationServices.searchLegacyProposal(query);
      setResults(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to search archive');
      } else {
        toast.error('Search failed. Legacy CRM may not be configured.');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewProposal = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.warning('No public link available for this proposal');
    }
  };

  const handleDownloadPdf = (proposalId) => {
    // Download PDF through our API proxy
    window.open(`/api/v1/integrations/legacy/proposal/${proposalId}/download`, '_blank');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box component="section" id="archive-page">
      <BrowserTitle title="Archive | Airmaxx Pro" />

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <StorageIcon sx={{ fontSize: 32, color: '#ed6c02' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Legacy CRM Archive
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Search for archived proposals from the old CRM system. This archive contains historical
          proposals needed for warranty service and reference.
        </Alert>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Search by PO Number or Address"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g., A25120301 or San Diego"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Enter PO number (e.g., A25120301) or part of the address"
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading || query.length < 2}
                sx={{ minWidth: 120, height: 56 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {results && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found <strong>{results.total?.toLocaleString()}</strong> proposal(s) matching "{query}"
          </Typography>

          {results.proposals?.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>PO Number</strong></TableCell>
                    <TableCell><strong>Client</strong></TableCell>
                    <TableCell><strong>Address</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.proposals.map((proposal) => (
                    <TableRow key={proposal.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {proposal.publicId}
                        </Typography>
                      </TableCell>
                      <TableCell>{proposal.clientName || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 250 }}>
                        <Typography variant="body2" noWrap title={proposal.address}>
                          {proposal.address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(proposal.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={proposal.status}
                          size="small"
                          {...(proposal.status === 'Cold' 
                            ? { sx: { backgroundColor: '#000', color: '#fff' } }
                            : { color: 
                                proposal.status === 'Completed' ? 'success' :
                                proposal.status === 'Active' ? 'primary' :
                                proposal.status === 'Pending' ? 'warning' : 'default'
                              }
                          )}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(proposal.totalPrice)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewProposal(proposal.url)}
                          title="View Proposal"
                          disabled={!proposal.url}
                          color="primary"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadPdf(proposal.publicId)}
                          title="Download PDF"
                          color="error"
                        >
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No proposals found matching "{query}"
              </Typography>
            </Paper>
          )}
        </Box>
      )}

    </Box>
  );
};

